import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useAppStore } from '@/stores/app'

/**
 * Layout state shared by `Default.vue` and its components (vue-spec `structure.md` —
 * `layouts/layout.ts`). Module-level, because there is one chrome per window: two copies of
 * "is the sidebar open" is how a menu closes itself on one component and not the other.
 *
 * Dark mode is deliberately NOT here, and it is not the topbar's either. Nuxt UI auto-registers
 * VueUse's `useDark`, and `stores/app.ts` wraps that SAME state as `colorMode` — one source of
 * truth, per vue-spec `theme.md`. The topbar only renders `ColorModeToggle`; duplicating the
 * state here would be the second source of truth theme.md forbids.
 */
/**
 * THE MENU, in one place. `Sidebar.vue` renders it and `Topbar.vue` reads the CURRENT entry's
 * label for the page title — two components, one list, so a section can never be named one
 * thing in the menu and another in the title bar. It lists only routes that EXIST.
 *
 * `routes` is every route of a section, so a page inside it (the editor inside Predloge) keeps
 * its section highlighted in the menu and titled in the topbar. The labels are i18n KEYS, not
 * prose: the strings stay in `libs/i18n/locales/*`.
 */
export interface NavItem {
    /** The route this entry navigates to. */
    name:     string
    /** The i18n key of its label (`nav.*`). */
    labelKey: string
    icon:     string
    /** Every route of this section. */
    routes:   string[]
    /** A tenant admin's entry (the tenant's users) — a provider admin has its own, all users. */
    tenantAdminOnly?: boolean
}

/**
 * A SECTION of the menu. `labelKey` is the small uppercase header above the group, or `null`
 * for the opening group.
 *
 * DIGITAL SIGNAGE: the opening group is the tenant's work (`tenant: true` — its links carry the
 * current tenant, `/t/:tid/…`, and it is left out while there is no tenant to work in); the
 * "Ponudnik" group is the provider admin's only (`providerOnly`).
 */
export interface NavGroup {
    labelKey:      string | null
    items:         NavItem[]
    tenant?:       boolean
    providerOnly?: boolean
}

export const NAV_GROUPS: NavGroup[] = [
    {
        labelKey: null,
        tenant:   true,
        items: [
            { name: 'overview',     labelKey: 'nav.overview',     icon: 'i-lucide-house',           routes: ['overview'] },
            { name: 'screens',      labelKey: 'nav.screens',      icon: 'i-lucide-monitor',         routes: ['screens'] },
            { name: 'media',        labelKey: 'nav.media',        icon: 'i-lucide-image',           routes: ['media'] },
            { name: 'menu',         labelKey: 'nav.menu',         icon: 'i-lucide-utensils',        routes: ['menu'] },
            { name: 'templates',    labelKey: 'nav.templates',    icon: 'i-lucide-layout-grid',     routes: ['templates', 'design-editor', 'playlist-editor'] },
            { name: 'schedules',    labelKey: 'nav.schedules',    icon: 'i-lucide-calendar',        routes: ['schedules'] },
            { name: 'brand',        labelKey: 'nav.brand',        icon: 'i-lucide-palette',         routes: ['brand'] },
            { name: 'tenant-users', labelKey: 'nav.tenant_users', icon: 'i-lucide-users',           routes: ['tenant-users'], tenantAdminOnly: true },
        ],
    },
    {
        labelKey:     'nav.group_provider',
        providerOnly: true,
        items: [
            { name: 'tenants', labelKey: 'nav.tenants', icon: 'i-lucide-building-2', routes: ['tenants'] },
            { name: 'users',   labelKey: 'nav.users',   icon: 'i-lucide-users',      routes: ['users'] },
        ],
    },
]

/** The entry at the bottom of the sidebar, above the user card (as in the mockups). */
export const SETTINGS_ITEM: NavItem = { name: 'settings', labelKey: 'nav.settings', icon: 'i-lucide-settings', routes: ['settings'] }

/** Every entry, flattened — what the topbar titles the page from. The groups are the model. */
export const NAV_ITEMS: NavItem[] = [...NAV_GROUPS.flatMap((group) => group.items), SETTINGS_ITEM]

/** The i18n key of the page title for a route, falling back to the product name. */
export const navTitleKey = (routeName: string): string =>
    NAV_ITEMS.find((item) => item.routes.includes(routeName))?.labelKey ?? 'brand.product_name'

/**
 * IS THE MOBILE DRAWER OPEN. Module-level, and the only copy — below 1024px the sidebar is not
 * a column any more but an overlay over the whole shell, and an overlay that two components
 * disagree about is an overlay that cannot be closed.
 *
 * It only ever MEANS anything on mobile. On the desktop the sidebar is rendered because it is
 * the layout's left column, not because this is `true`, which is why `useLayoutShell()` forces
 * it back to `false` the moment the window grows past the breakpoint: a flag left set by a
 * phone-width session would otherwise re-open the drawer the next time the window narrows.
 */
const menuOpen = ref(false)

const openMenu = (): void => {
    menuOpen.value = true
}

const closeMenu = (): void => {
    menuOpen.value = false
}

const toggleMenu = (): void => {
    menuOpen.value = !menuOpen.value
}

/**
 * The state and the ONE action per verb. Every component that can open or close the drawer —
 * the topbar's hamburger, the drawer's own X, a nav row, the backdrop — calls one of these
 * three; none of them writes `menuOpen` itself.
 */
export const useLayout = () => {
    const appStore = useAppStore()

    const isMobile = computed((): boolean => appStore.isMobile())

    return { menuOpen, isMobile, openMenu, closeMenu, toggleMenu }
}

/**
 * THE DRAWER'S BEHAVIOUR, wired once by `Default.vue` and nowhere else.
 *
 * A drawer that can be opened and not closed is what the shell shipped with, so the four ways
 * out live together here rather than one per component: Escape, a route change, growing past
 * the breakpoint, and — in the layout, because it is a paint and not a behaviour — the
 * backdrop. While it is open the body cannot scroll, or the page behind the overlay scrolls
 * under the thumb instead of the menu.
 *
 * The listener is on `document` for the whole life of the shell rather than added and removed
 * around `menuOpen`: one registration, no window where a key lands between the watcher and the
 * listener. It does nothing at all unless the drawer is open.
 */
export const useLayoutShell = () => {
    const { isMobile } = useLayout()
    const route        = useRoute()

    /** What `body` had before the drawer took it — restored verbatim, not blanked. */
    let bodyOverflow: string | null = null

    const lockBody = (): void => {
        if (bodyOverflow === null) {
            bodyOverflow = document.body.style.overflow
        }

        document.body.style.overflow = 'hidden'
    }

    const unlockBody = (): void => {
        if (bodyOverflow === null) {
            return
        }

        document.body.style.overflow = bodyOverflow
        bodyOverflow = null
    }

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' && menuOpen.value) {
            closeMenu()
        }
    }

    // Growing past the breakpoint: the sidebar is the layout's own column again, so the flag
    // that made it an overlay has to go with it.
    watch(isMobile, (mobile): void => {
        if (!mobile) {
            closeMenu()
        }
    })

    // Navigating is the most common way out of the menu — the row that was tapped has already
    // done its job. `Sidebar.vue` closes on select too; this catches every other route change
    // (a redirect, the back button, a link inside the page).
    watch(() => route.path, (): void => {
        closeMenu()
    })

    watch([menuOpen, isMobile], ([open, mobile]): void => {
        if (open && mobile) {
            lockBody()
        } else {
            unlockBody()
        }
    })

    onMounted((): void => {
        document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount((): void => {
        document.removeEventListener('keydown', onKeydown)
        unlockBody()
    })
}
